'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import { slugify } from '@/utils/slugify';
import { CopyLink } from '@/components/CopyLink';
import { useGaContext } from '@/context/GoogleAnalyticsContext';

import styles from './styles.module.css';

interface TableOfContentProps {
	className?: string;
	items?: { title: string; url: string }[];
	ordered?: boolean;
	sort?: 'ascending' | 'descending';
}

export const TableOfContent: FC<TableOfContentProps> = ({
	className,
	items = [],
	ordered = false,
	sort = 'ascending',
}) => {
	const { sendGaEvent } = useGaContext('StructuredListToC');
	const handleAnchorClick = (header: string) => () => {
		sendGaEvent('structured_list_anchor_click', {
			nonInteraction: false,
			placement: header,
			widgetId: 'content-list-table-of-contents',
		});
	};

	const List = ordered ? 'ol' : 'ul';

	const hasItems = items && items.length > 0;
	if (!hasItems) {
		return null;
	}

	return (
		<nav
			aria-label="Structured List Table of Contents"
			className={clsx(styles.tableOfContent, className)}
		>
			<h2 className={styles.tableOfContentTitle}>Table of Contents</h2>
			<List className={styles.tableOfContentList}>
				{items.map((item, index) => (
					<li
						className={styles.tableOfContentItem}
						key={`toc-item-${slugify(item.title)}`}
					>
						<CopyLink
							className={styles.tableOfContentLink}
							href={item.url}
							onClick={handleAnchorClick(item.title)}
						>
							<span className={styles.tableOfContentText}>{item.title}</span>
							{ordered && (
								<span className={styles.tableOfContentNumber}>
									{String(
										sort === 'ascending' ? index + 1 : items.length - index,
									).padStart(2, '0')}
								</span>
							)}
						</CopyLink>
					</li>
				))}
			</List>
		</nav>
	);
};
